import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { z } from "zod";
import { createNoIndexMeta } from "~/lib/shared/meta";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  ensureCoachDetails,
  getCoachProfile,
  updateCoachDetails,
  updateCoachProfile,
} from "~/lib/server/coach";
import {
  getCoachExpertise,
  getExpertiseSubcategories,
  updateCoachExpertise,
} from "~/lib/functions/expertise";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import { getDBWithInit } from "~/lib/db";
import { requireCoachAuth } from "~/lib/coach-middleware";
import {
  FormAdvancedCheckboxGroupField,
  FormCheckboxField,
  FormCheckboxGroupField,
  FormInputField,
  FormRadioGroupField,
  FormSelectField,
  FormSingleCheckboxField,
} from "~/components/form/form-fields";

export const CoachDetailsSchema = z.object({
  id: z.string(),
  coachId: z.string(),
  username: z.string(),
  dateOfBirth: z.string().optional().nullable(),
  gender: z.string().optional().nullable(),

  // Coaching Focus & Expertise
  primaryExpertise: z.array(z.string()).max(3).default([]),
  secondaryExpertise: z.array(z.string()).max(3).default([]),

  // Coaching Format & Delivery
  coachingFormats: z.array(z.string()).min(1),
  sessionDeliveryMethods: z.array(z.string()).min(1),

  // Experience Level & Coaching Packages
  experienceLevel: z.string().optional().nullable(),

  // Coaching Style & Approach
  coachingApproaches: z.array(z.string()).max(2).default([]),

  // Faith-Based Coaching
  faithIntegration: z.array(z.string()).default([]),

  // Personality Profile
  discType: z.string().optional().nullable(),
  mbtiType: z.string().optional().nullable(),
  enneagramType: z.string().optional().nullable(),
  humanDesignType: z.string().optional().nullable(),

  isPublic: z.boolean(),
});

type FormData = z.infer<typeof CoachDetailsSchema>;

export const getCoachDetailsData = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const details = await (
      await getDBWithInit()
    ).query.coachDetails.findFirst({
      where: (coachDetails, { eq }) => eq(coachDetails.coachId, coach.id),
    });

    if (!details) {
      throw new Error("Coach details not found");
    }

    if (!coach.profileId) {
      throw new Error("Coach has no profile");
    }

    const profile = await getCoachProfile(coach.profileId);

    if (!profile) {
      throw new Error("Coach profile not found");
    }

    // Get coach expertise
    const expertise = await getCoachExpertise({ data: { coachId: coach.id } });
    const primaryExpertise = expertise
      .filter((item) => "primary" === item.type)
      .map((item) => item.subcategoryId.toString());
    const secondaryExpertise = expertise
      .filter((item) => "secondary" === item.type)
      .map((item) => item.subcategoryId.toString());

    // Ensure all array fields are never null
    const transformedDetails = {
      ...details,
      primaryExpertise,
      secondaryExpertise,
      coachingFormats: details.coachingFormats || [],
      sessionDeliveryMethods: details.sessionDeliveryMethods || [],
      coachingApproaches: details.coachingApproaches || [],
      faithIntegration: details.faithIntegration || [],
    };

    return {
      ...transformedDetails,
      username: coach.username,
      dateOfBirth:
        transformedDetails.dateOfBirth &&
        new Intl.DateTimeFormat("en-CA").format(transformedDetails.dateOfBirth),
      isPublic: profile.isPublic,
    };
  });

export const getExpertiseOptions = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async () => {
    return await getExpertiseSubcategories();
  });

export const saveCoachDetails = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator((data: unknown) => CoachDetailsSchema.parse(data))
  .handler(async ({ data, context }) => {
    const coach = context.coach;
    const detailsId = data.id ? data.id : undefined;
    const details = {
      ...data,
      dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : null,
    };

    // Ensure details exist and get the ID, then update with form data
    const ensuredDetails = await ensureCoachDetails(coach.id, detailsId);

    if (!ensuredDetails) {
      throw new Error(`Could not ensure coach details for coach ${coach.id}`);
    }

    // Now update the (potentially newly created) details with the form data
    await updateCoachDetails(ensuredDetails.id, details);

    await updateCoachProfile(coach.id, { isPublic: data.isPublic });

    // Update coach expertise
    await updateCoachExpertise({
      data: {
        coachId: coach.id,
        primaryExpertise: data.primaryExpertise,
        secondaryExpertise: data.secondaryExpertise,
      },
    });

    return {
      status: "success",
      message: "Coach details saved",
    };
  });

export const Route = createFileRoute("/coach/_backend/edit-profile/details/")({
  head: () =>
    createNoIndexMeta(
      "Edit Details – Dream Coach Match",
      "Edit your coaching details and specializations",
    ),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["coach", "details"],
        queryFn: () => getCoachDetailsData(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["expertise", "options"],
        queryFn: () => getExpertiseOptions(),
      }),
    ]);
    return {};
  },
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { data: coachData } = useQuery({
    queryKey: ["coach", "details"],
    queryFn: () => getCoachDetailsData(),
  });

  const { data: expertiseOptions = [] } = useQuery({
    queryKey: ["expertise", "options"],
    queryFn: () => getExpertiseOptions(),
  });

  const { register, handleSubmit, formState, watch, setValue, reset, control } =
    useForm<FormData>({
      resolver: zodResolver(CoachDetailsSchema),
      defaultValues: coachData,
    });

  const { isDirty } = formState;

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => saveCoachDetails({ data }),
    onSuccess: (_, variables) => {
      toast.success("Coach details saved successfully");
      queryClient.invalidateQueries({ queryKey: ["coach"] });
      reset(variables);
    },
    onError: () => {
      toast.error("Failed to save coach details");
    },
  });

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const { showExitAlert, onDiscard } = useUnsavedChangesAlert(() => isDirty);

  // Watch for expertise changes to show remaining counts
  const watchedPrimaryExpertise = watch("primaryExpertise");
  const watchedSecondaryExpertise = watch("secondaryExpertise");

  const handleFaithIntegrationChange = (
    values: string[],
    selectedValue: string,
  ) => {
    if (
      "I do not integrate spirituality into my coaching" === selectedValue &&
      values.includes(selectedValue)
    ) {
      // When "no integration" is selected, clear all other options
      return ["I do not integrate spirituality into my coaching"];
    }
    // When any other option is selected, remove "no integration"
    return values.filter(
      (v) => v !== "I do not integrate spirituality into my coaching",
    );
  };

  const expertiseByCategory = React.useMemo(() => {
    const map = new Map<string, typeof expertiseOptions>();
    expertiseOptions.forEach((option) => {
      if (!option.categoryName) {
        return;
      }
      if (!map.has(option.categoryName)) {
        map.set(option.categoryName, []);
      }
      map.get(option.categoryName)!.push(option);
    });
    return map;
  }, [expertiseOptions]);

  if (!coachData) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <UnsavedChangesAlert show={showExitAlert} onDiscard={onDiscard} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <ProfileEditHeader
          title="Matching Details"
          description="Private details that will help our algorithm match you with the right clients."
          username={coachData.username}
          form={formState}
        />

        <Card>
          <CardHeader>
            <CardTitle>Demographics</CardTitle>
            <CardDescription>
              Sharing demographic information is optional. If you choose to
              provide it, this information will not be directly displayed on
              your public profile. Instead, it will be used to enhance the
              matching process by helping clients find a coach that aligns with
              their preferences (e.g., age range or gender). As a result, your
              profile may appear in a way that suggests demographic alignment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input {...register("coachId")} type="hidden" />
            <input {...register("id")} type="hidden" />
            <input {...register("username")} type="hidden" />

            <FormInputField
              control={control}
              name="dateOfBirth"
              label="Date of Birth"
              helpText="Your date of birth"
              type="date"
              isPrivate
            />

            <FormSelectField
              control={control}
              name="gender"
              label="Gender"
              helpText="Your gender"
              options={[
                "Woman",
                "Man",
                "Non-Binary",
                "Prefer to self-describe",
                "Prefer not to say",
              ]}
              placeholder="Select gender"
              isPrivate
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coaching Focus & Expertise</CardTitle>
            <CardDescription>
              What areas do you specialize in? (Select all that apply) These
              selections determine which client goals you'll be matched with.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {Array.from(expertiseByCategory.entries()).map(
              ([categoryName, options], index) => (
                <div key={categoryName} className="grid grid-cols-1 gap-4">
                  <div className="overflow-x-auto">
                    <div className="w-full">
                      {/* Header */}
                      <div className="flex border-b border-gray-200 py-3">
                        <div className="text-primary flex-1 text-left text-sm font-medium">
                          {categoryName}
                        </div>
                        <div className="text-secondary w-32 text-center text-sm">
                          Primary
                          {0 === index && (
                            <div className="text-xs font-normal">
                              (max 3,{" "}
                              {3 - (watchedPrimaryExpertise?.length || 0)} left)
                            </div>
                          )}
                        </div>
                        <div className="text-secondary w-32 text-center text-sm">
                          Secondary
                          {0 === index && (
                            <div className="text-xs font-normal">
                              (max 3,{" "}
                              {3 - (watchedSecondaryExpertise?.length || 0)}{" "}
                              left)
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Rows */}
                      {options.map((opt) => (
                        <div
                          key={opt.id}
                          className="flex items-center border-b border-gray-100 hover:bg-gray-50"
                        >
                          <div className="flex-1 py-3 pl-1 text-sm">
                            {opt.name}
                          </div>
                          <div className="flex w-32 justify-center py-2">
                            <div className="-my-2">
                              <FormSingleCheckboxField
                                control={control}
                                name="primaryExpertise"
                                value={opt.id.toString()}
                                currentValues={watchedPrimaryExpertise}
                                maxCount={3}
                              />
                            </div>
                          </div>
                          <div className="flex w-32 justify-center py-2">
                            <div className="-my-2">
                              <FormSingleCheckboxField
                                control={control}
                                name="secondaryExpertise"
                                value={opt.id.toString()}
                                currentValues={watchedSecondaryExpertise}
                                maxCount={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ),
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coaching Format & Delivery</CardTitle>
            <CardDescription>
              How do you deliver your coaching services?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormCheckboxGroupField
              control={control}
              name="coachingFormats"
              label="What coaching format do you offer? (Select all that apply)"
              options={[
                { value: "1-on-1", label: "1:1 Coaching" },
                { value: "group", label: "Group Coaching" },
              ]}
            />

            <FormCheckboxGroupField
              control={control}
              name="sessionDeliveryMethods"
              label="How do you deliver your coaching sessions? (Select all that apply)"
              options={[
                {
                  value: "online",
                  label: "Online (Zoom, Google Meet, etc.)",
                },
                {
                  value: "in-person",
                  label: "In-person",
                },
                {
                  value: "hybrid",
                  label: "Hybrid (Mix of online & in-person coaching)",
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Experience Level & Coaching Packages</CardTitle>
            <CardDescription>
              Which coaching package best represents your level of experience?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormRadioGroupField
              control={control}
              name="experienceLevel"
              options={[
                {
                  value: "Rising Coach",
                  label: "Rising Coach (Goal-Driven Starter Package)",
                  description:
                    "For early-stage clients looking for clarity & momentum.",
                },
                {
                  value: "Experienced Coach",
                  label: "Experienced Coach (Proven Results Package)",
                  description:
                    "For structured coaching with a seasoned professional.",
                },
                {
                  value: "Master Coach",
                  label: "Master Coach (Elite Transformation Partnership)",
                  description: "For deep transformation & long-term strategy.",
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coaching Style & Approach</CardTitle>
            <CardDescription>
              How would you describe your coaching approach? (Select up to 2)
              This helps match you with clients who prefer your coaching style.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormAdvancedCheckboxGroupField
              control={control}
              name="coachingApproaches"
              options={[
                {
                  value: "Exploratory & Self-Reflective",
                  label: "Self-discovery & deep reflection",
                  description: "Transformational Coaching, Therapy, Healing.",
                },
                {
                  value: "Balanced guidance & structured advice",
                  label: "Balanced guidance & structured advice",
                  description:
                    "Mentoring, Balanced Coaching – a mix of exploration & expert input.",
                },
                {
                  value: "Goal-oriented & action-driven",
                  label: "Goal-oriented & action-driven",
                  description:
                    "Consulting, Business Coaching, Executive Coaching – structured, clear strategies.",
                },
                {
                  value: "Supportive & Encouraging",
                  label: "Supportive & motivational coaching",
                  description:
                    "Wellness Coaching, Relationship Coaching – motivational, compassionate, accountability-driven.",
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Faith & Spirituality in Coaching</CardTitle>
            <CardDescription>
              Do you integrate faith or spirituality into your coaching? Select
              any faith-based approaches you incorporate in your coaching. This
              helps match you with clients who seek a spiritually aligned
              coaching experience.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormAdvancedCheckboxGroupField
              control={control}
              name="faithIntegration"
              options={[
                { value: "Christianity", label: "Christianity" },
                { value: "Islam", label: "Islam" },
                { value: "Judaism", label: "Judaism" },
                { value: "Hinduism", label: "Hinduism" },
                { value: "Buddhism", label: "Buddhism" },
                {
                  value: "Spiritual but not Religious",
                  label: "Spiritual but not Religious",
                },
                {
                  value: "I do not integrate spirituality into my coaching",
                  label: "I do not integrate spirituality into my coaching",
                },
              ]}
              onValueChange={(values, changedValue) => {
                const newValues = handleFaithIntegrationChange(
                  values,
                  changedValue,
                );
                setValue("faithIntegration", newValues);
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Personality Profile</CardTitle>
            <CardDescription>
              We use personality insights to help match you with clients who
              align with your coaching style. If you're unsure about your
              personality types, feel free to leave this section blank.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormSelectField
              control={control}
              name="discType"
              label="DISC Personality Type"
              options={[
                "D",
                "I",
                "S",
                "C",
                "DI",
                "DC",
                "DS",
                "ID",
                "IC",
                "IS",
                "SD",
                "SI",
                "SC",
                "CD",
                "CI",
                "CS",
              ]}
              placeholder="Select DISC type"
            />

            <FormSelectField
              control={control}
              name="mbtiType"
              label="Myers-Briggs (MBTI) Type"
              options={[
                "INTJ",
                "INTP",
                "ENTJ",
                "ENTP",
                "INFJ",
                "INFP",
                "ENFJ",
                "ENFP",
                "ISTJ",
                "ISFJ",
                "ESTJ",
                "ESFJ",
                "ISTP",
                "ISFP",
                "ESTP",
                "ESFP",
              ]}
              placeholder="Select MBTI type"
            />

            <FormSelectField
              control={control}
              name="enneagramType"
              label="Enneagram Type"
              options={[
                "Type 1w9",
                "Type 1w2",
                "Type 2w1",
                "Type 2w3",
                "Type 3w2",
                "Type 3w4",
                "Type 4w3",
                "Type 4w5",
                "Type 5w4",
                "Type 5w6",
                "Type 6w5",
                "Type 6w7",
                "Type 7w6",
                "Type 7w8",
                "Type 8w7",
                "Type 8w9",
                "Type 9w8",
                "Type 9w1",
              ]}
              placeholder="Select Enneagram type"
            />

            <FormSelectField
              control={control}
              name="humanDesignType"
              label="Human Design Type"
              options={[
                "Manifestor",
                "Generator",
                "Manifesting Generator",
                "Projector",
                "Reflector",
              ]}
              placeholder="Select Human Design type"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profile Visibility</CardTitle>
            <CardDescription>
              Control whether your profile is visible to the public. When
              enabled, your profile will be indexed by search engines like
              Google.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormCheckboxField control={control} name="isPublic">
              Make my profile public
            </FormCheckboxField>
          </CardContent>
        </Card>
      </form>
    </>
  );
}
